import React, { useEffect, useRef, useState } from "react";
import { BsFillPauseFill, BsFillPlayFill, BsVolumeUpFill, BsVolumeDownFill, BsFillVolumeOffFill, BsVolumeMuteFill } from "react-icons/bs";
import { RiVolumeUpFill } from "react-icons/ri";
import WaveSurfer from "wavesurfer.js";

const Media = ({ media }) => {
  const wavesurfer = useRef(null);
  const waveformRef = useRef(null);

  const [volume, setVolume] = useState(1);
  const [mute, setMute] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!waveformRef.current || !media) return;

    setElapsed(0);
    setDuration(0);
    setIsPlaying(false);
    setIsReady(false);
    setHasError(false);

    const ws = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#d1d5db",
      progressColor: "#f97316",
      cursorColor: "transparent",
      barWidth: 3,
      barGap: 2,
      barRadius: 3,
      height: 64,
      normalize: true,
      url: media,
    });

    wavesurfer.current = ws;

    ws.on("ready", () => {
      console.log("Voice ready:", media);

      setDuration(ws.getDuration());
      ws.setVolume(mute ? 0 : volume);
      setIsReady(true);
    });

    ws.on("error", (error) => {
      console.error("WaveSurfer error:", error, media);

      setHasError(true);
      setIsReady(false);
      setIsPlaying(false);
    });

    ws.on("play", () => {
      setIsPlaying(true);
    });

    ws.on("pause", () => {
      setIsPlaying(false);
    });

    ws.on("finish", () => {
      setIsPlaying(false);
      setElapsed(0);
    });

    ws.on("audioprocess", () => {
      setElapsed(ws.getCurrentTime());
    });

    ws.on("seeking", () => {
      setElapsed(ws.getCurrentTime());
    });

    return () => {
      ws.destroy();

      if (wavesurfer.current === ws) {
        wavesurfer.current = null;
      }
    };
  }, [media]);

  const formatTime = (sec) => {
    if (!Number.isFinite(sec) || sec < 0) return "00:00";

    const minutes = Math.floor(sec / 60).toString().padStart(2, "0");
    const seconds = Math.floor(sec % 60).toString().padStart(2, "0");

    return `${minutes}:${seconds}`;
  };

  const handlePlayPause = async () => {
    const ws = wavesurfer.current;

    if (!ws || !isReady || hasError) return;

    try {
      await ws.playPause();
    } catch (error) {
      console.error("Voice playback error:", error);
    }
  };

  const handleMute = () => {
    const ws = wavesurfer.current;

    if (!ws) return;

    const newMute = !mute;

    setMute(newMute);
    ws.setVolume(newMute ? 0 : volume);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);

    setVolume(newVolume);

    if (newVolume > 0) {
      setMute(false);
    }

    wavesurfer.current?.setVolume(newVolume);
  };

  const VolumeBtns = () => {
    let Icon;

    if (mute) Icon = BsVolumeMuteFill;
    else if (volume <= 0.01) Icon = BsFillVolumeOffFill;
    else if (volume <= 0.25) Icon = BsVolumeDownFill;
    else if (volume <= 0.75) Icon = RiVolumeUpFill;
    else Icon = BsVolumeUpFill;

    return (
      <button type="button" onClick={handleMute} disabled={!isReady || hasError} className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition hover:bg-orange-50 hover:text-orange-600 disabled:cursor-not-allowed disabled:opacity-50" aria-label={mute ? "Unmute" : "Mute"}>
        <Icon size={20} />
      </button>
    );
  };

  return (
    <div className="flex w-full flex-1 flex-col gap-3">
      {/* Player */}

      <div className="flex w-full items-center gap-3">
        {/* Play / Pause */}

        <button type="button" onClick={handlePlayPause} disabled={!isReady || hasError} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white shadow-sm transition-all hover:bg-orange-600 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50" aria-label={isPlaying ? "Pause recording" : "Play recording"}>
          {isPlaying ? <BsFillPauseFill className="h-6 w-6" /> : <BsFillPlayFill className="ml-0.5 h-6 w-6" />}
        </button>

        {/* Waveform */}

        <div className="min-w-0 flex-1">
          <div ref={waveformRef} className="w-full" />

          {hasError && (
            <p className="mt-1 text-xs text-red-500">
              Unable to load this voice recording.
            </p>
          )}
        </div>
      </div>

      {/* Bottom controls */}

      <div className="flex w-full items-center justify-between">
        {/* Time */}

        <p className="whitespace-nowrap text-xs font-medium text-gray-400">
          {formatTime(elapsed)}
          <span className="mx-1 text-gray-300">/</span>
          {formatTime(duration)}
        </p>

        {/* Volume */}

        <div className="flex items-center gap-1.5">
          <VolumeBtns />

          <input type="range" min="0" max="1" step="0.01" value={mute ? 0 : volume} onChange={handleVolumeChange} disabled={!isReady || hasError} className="h-1 w-20 cursor-pointer accent-orange-500 disabled:cursor-not-allowed disabled:opacity-50" />
        </div>
      </div>
    </div>
  );
};

export default Media;